// src/components/SubPayDirections.js
// The "Directions" popup for subcontractor daily / project pay.
// Rendered into document.body (a portal) so it always opens as a real popup
// on top of the page, even from inside a job's details window.

import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import "./SubPayPanel.css";

const SubPayDirections = ({ open, onClose }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || typeof document === "undefined") return null;
  return createPortal(
    <div
      className="spp-overlay"
      onClick={(e) => {
        // stop here so the click never reaches the job window behind it
        e.stopPropagation();
        onClose();
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="spp-dialog" role="dialog" aria-label="Sub contractor pay directions" onClick={(e) => e.stopPropagation()}>
        <div className="spp-dialog__head">
          <h3>Sub Contractor Pay: Directions</h3>
          <button type="button" className="spp-link" onClick={onClose}>
            Close ✕
          </button>
        </div>

        <ol className="spp-steps">
          <li>
            <b>Set up the person once (Staff).</b> Open Staff, edit the person and choose{" "}
            <b>Sub Contractor</b>. Fill in their company name if they have one. Optional: set{" "}
            <b>Default $ per day per worker</b> (blank = $350). Only Sub Contractors get the pay options;
            employees never do.
          </li>
          <li>
            <b>Assign them to the job.</b> Add them once under <b>Tech Assign</b> (Scheduling, or Edit on the job).
            Right under the tech list you'll see <b>Sub Contractor pay</b>. Click <b>Daily</b> or <b>Project</b>.
            <ul>
              <li>
                <b>Daily:</b> enter how many <b>workers</b> they bring and the <b>$ per day per worker</b>. Days
                are counted from the job's start and end dates. Type a number in Days only if it's different.
                Example: 3 workers × $350 × 2 days = $2,100.
              </li>
              <li>
                <b>Project:</b> enter one price for the whole job.
              </li>
            </ul>
            Don't add the sub's workers as techs. The worker count covers them.
          </li>
          <li>
            <b>Save the job.</b> The amount owed is added to the job right away and shows everywhere the
            back end shows sub pay: Contractor Pay, Job Estimate Spreadsheet, dashboards.
          </li>
          <li>
            <b>Continue.</b> When you Continue the job, the pay rolls over to the new work order:
            <ul>
              <li><b>Daily:</b> same crew and rate, and it counts the new job's days.</li>
              <li>
                <b>Project:</b> the price is owed <b>once</b>, on the first job. Continued jobs show the price
                but add $0, until the job is finally marked Completed.
              </li>
            </ul>
          </li>
          <li>
            <b>Pay them.</b> Open the job, then <b>Sub Pay</b>, then <b>Record payment</b>: amount, date, how
            (Check, ACH, Cash…) and check #. Payments recorded in Contractor Pay or the Job Estimate
            Spreadsheet against that job show here too. It's all the same records.
          </li>
          <li>
            <b>Check the balance.</b> Each sub shows <b>Owed</b>, <b>Paid</b> and <b>Balance</b>, plus every
            payment with its date and method. For project pay on a continued job, payments go to the first
            job, where the price is owed.
          </li>
          <li>
            <b>Fix mistakes.</b> Change workers, rate, days or price on the job and save. The owed amount
            updates. Payments can be edited or deleted in Sub Pay. If someone changes the owed amount on the
            back end, that amount is kept, and Sub Pay shows a note with a <b>Use terms amount</b> button.
          </li>
          <li>
            <b>Going back to old jobs (clean-up).</b> Team Dashboard → <b>Sub Pay Setup</b> lists every job with a
            Sub Contractor on it. Use the filters (sub, status, "Needs pay set"), click a job's <b>Sub Pay</b> and set
            Daily or Project. Payments already recorded against that job show up automatically.
            <ul>
              <li>
                For a job that was continued (3101 → 3101-2 → 3101-3), set the pay on the <b>first</b> job, then
                press <b>Copy to continued jobs</b>. Daily fills in each continued job's own days. Project shows the
                price on each one but counts it once, on the first job.
              </li>
              <li>
                You can also do it from any job: open the job, then <b>Sub Pay</b> (or <b>Edit</b> → under Tech Assign).
              </li>
            </ul>
          </li>
          <li>
            <b>What the sub sees.</b> When the sub contractor signs in, Home shows <b>My Pay</b>: what
            they've earned on each job, every payment (when and how), and what's still owed to them. On a job
            they only see their own pay.
          </li>
          <li>
            <b>Who can see it.</b> Only Admin sees sub pay on jobs. Managers, Assistants and other techs
            don't.
          </li>
        </ol>
      </div>
    </div>,
    document.body
  );
};

export default SubPayDirections;
